// Comprehensive Indicator Registry for Macro Monitor
// 200+ macroeconomic indicators organized by category

export type IndicatorCategory =
  | 'gdp_growth'
  | 'prices'
  | 'external'
  | 'fiscal'
  | 'monetary'
  | 'labor'
  | 'demographics'
  | 'business'
  | 'ratings';

export type IndicatorFrequency = 'annual' | 'quarterly' | 'monthly' | 'daily';
export type IndicatorDirection = 'up' | 'down' | 'neutral';
export type DataSource = 'IMF' | 'World Bank' | 'CBU' | 'NATEX' | 'Trading Economics' | 'UN' | 'ILO';

export interface IndicatorMetadata {
  id: string;
  name: string;
  description: string;
  unit: string;
  scale: number;
  frequency: IndicatorFrequency;
  source: DataSource;
  sourceCode?: string;
  wbCode?: string;
  imfCode?: string;
  category: IndicatorCategory;
  subcategory?: string;
  goodDirection: IndicatorDirection;
  thresholds?: { low: number; high: number };
  methodology?: string;
  relatedIndicators?: string[];
  isPrimary?: boolean;
}

export interface CategoryInfo {
  id: IndicatorCategory;
  name: string;
  description: string;
  icon: string;
  color: string;
}

// Category definitions
export const CATEGORIES: CategoryInfo[] = [
  {
    id: 'gdp_growth',
    name: 'GDP & Growth',
    description: 'Economic output, growth rates, and structural composition',
    icon: 'TrendingUp',
    color: '#00d26a',
  },
  {
    id: 'prices',
    name: 'Prices & Inflation',
    description: 'Consumer prices, producer prices, and inflation metrics',
    icon: 'DollarSign',
    color: '#ff3b3b',
  },
  {
    id: 'external',
    name: 'External Sector',
    description: 'Trade, current account, FDI, and reserves',
    icon: 'Globe',
    color: '#00a3ff',
  },
  {
    id: 'fiscal',
    name: 'Fiscal',
    description: 'Government budget, debt, and public finance',
    icon: 'Building',
    color: '#ffb800',
  },
  {
    id: 'monetary',
    name: 'Monetary & Financial',
    description: 'Interest rates, money supply, and banking sector',
    icon: 'Landmark',
    color: '#a855f7',
  },
  {
    id: 'labor',
    name: 'Labor Market',
    description: 'Employment, wages, and labor force statistics',
    icon: 'Users',
    color: '#06b6d4',
  },
  {
    id: 'demographics',
    name: 'Demographics & Social',
    description: 'Population, urbanization, and social indicators',
    icon: 'UserCircle',
    color: '#ec4899',
  },
  {
    id: 'business',
    name: 'Business Environment',
    description: 'Ease of doing business, competitiveness, and reforms',
    icon: 'Briefcase',
    color: '#f97316',
  },
  {
    id: 'ratings',
    name: 'Ratings & Risk',
    description: 'Sovereign ratings, country risk, and ESG scores',
    icon: 'Shield',
    color: '#14b8a6',
  },
];

// Comprehensive indicator registry (200+ indicators)
export const INDICATOR_REGISTRY: IndicatorMetadata[] = [
  // ═══════════════════════════════════════════════════════════════
  // GDP & GROWTH (25 indicators)
  // ═══════════════════════════════════════════════════════════════
  {
    id: 'gdp_nominal',
    name: 'GDP (Nominal)',
    description: 'Gross Domestic Product at current prices in local currency',
    unit: 'UZS trillions',
    scale: 1e12,
    frequency: 'annual',
    source: 'IMF',
    imfCode: 'NGDP',
    wbCode: 'NY.GDP.MKTP.CN',
    category: 'gdp_growth',
    subcategory: 'Output',
    goodDirection: 'up',
    isPrimary: true,
  },
  {
    id: 'gdp_usd',
    name: 'GDP (USD)',
    description: 'Gross Domestic Product in US dollars at market exchange rate',
    unit: 'USD billions',
    scale: 1e9,
    frequency: 'annual',
    source: 'IMF',
    imfCode: 'NGDPD',
    wbCode: 'NY.GDP.MKTP.CD',
    category: 'gdp_growth',
    subcategory: 'Output',
    goodDirection: 'up',
    isPrimary: true,
  },
  {
    id: 'gdp_growth',
    name: 'Real GDP Growth',
    description: 'Annual percentage change in real GDP',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'IMF',
    imfCode: 'NGDP_RPCH',
    wbCode: 'NY.GDP.MKTP.KD.ZG',
    category: 'gdp_growth',
    subcategory: 'Growth',
    goodDirection: 'up',
    thresholds: { low: 3, high: 7 },
    isPrimary: true,
  },
  {
    id: 'gdp_per_capita',
    name: 'GDP per Capita',
    description: 'GDP per capita in current US dollars',
    unit: 'USD',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'NY.GDP.PCAP.CD',
    category: 'gdp_growth',
    subcategory: 'Output',
    goodDirection: 'up',
    isPrimary: true,
  },
  {
    id: 'gdp_per_capita_ppp',
    name: 'GDP per Capita (PPP)',
    description: 'GDP per capita based on purchasing power parity',
    unit: 'Int$',
    scale: 1,
    frequency: 'annual',
    source: 'IMF',
    imfCode: 'PPPPC',
    wbCode: 'NY.GDP.PCAP.PP.CD',
    category: 'gdp_growth',
    subcategory: 'Output',
    goodDirection: 'up',
  },
  {
    id: 'gdp_growth_quarterly',
    name: 'GDP Growth (Quarterly)',
    description: 'Quarter-on-quarter GDP growth rate',
    unit: '%',
    scale: 1,
    frequency: 'quarterly',
    source: 'CBU',
    category: 'gdp_growth',
    subcategory: 'Growth',
    goodDirection: 'up',
  },
  {
    id: 'gni_usd',
    name: 'GNI (USD)',
    description: 'Gross National Income in US dollars',
    unit: 'USD billions',
    scale: 1e9,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'NY.GNP.MKTP.CD',
    category: 'gdp_growth',
    subcategory: 'Output',
    goodDirection: 'up',
  },
  {
    id: 'gni_per_capita',
    name: 'GNI per Capita',
    description: 'Gross National Income per capita',
    unit: 'USD',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'NY.GNP.PCAP.CD',
    category: 'gdp_growth',
    subcategory: 'Output',
    goodDirection: 'up',
  },
  {
    id: 'agriculture_gdp',
    name: 'Agriculture (% of GDP)',
    description: 'Agriculture, forestry, and fishing value added as share of GDP',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'NV.AGR.TOTL.ZS',
    category: 'gdp_growth',
    subcategory: 'Structure',
    goodDirection: 'neutral',
  },
  {
    id: 'industry_gdp',
    name: 'Industry (% of GDP)',
    description: 'Industry including construction value added as share of GDP',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'NV.IND.TOTL.ZS',
    category: 'gdp_growth',
    subcategory: 'Structure',
    goodDirection: 'up',
  },
  {
    id: 'services_gdp',
    name: 'Services (% of GDP)',
    description: 'Services value added as share of GDP',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'NV.SRV.TOTL.ZS',
    category: 'gdp_growth',
    subcategory: 'Structure',
    goodDirection: 'up',
  },
  {
    id: 'manufacturing_gdp',
    name: 'Manufacturing (% of GDP)',
    description: 'Manufacturing value added as share of GDP',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'NV.IND.MANF.ZS',
    category: 'gdp_growth',
    subcategory: 'Structure',
    goodDirection: 'up',
  },
  {
    id: 'consumption_gdp',
    name: 'Final Consumption (% of GDP)',
    description: 'Final consumption expenditure as share of GDP',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'NE.CON.TOTL.ZS',
    category: 'gdp_growth',
    subcategory: 'Demand',
    goodDirection: 'neutral',
  },
  {
    id: 'investment_gdp',
    name: 'Gross Capital Formation (% of GDP)',
    description: 'Gross capital formation as share of GDP',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'NE.GDI.TOTL.ZS',
    category: 'gdp_growth',
    subcategory: 'Demand',
    goodDirection: 'up',
    thresholds: { low: 20, high: 35 },
  },
  {
    id: 'gross_savings',
    name: 'Gross Savings (% of GDP)',
    description: 'Gross domestic savings as share of GDP',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'NY.GDS.TOTL.ZS',
    category: 'gdp_growth',
    subcategory: 'Demand',
    goodDirection: 'up',
  },
  {
    id: 'potential_gdp_growth',
    name: 'Potential GDP Growth',
    description: 'Estimated potential GDP growth rate',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'IMF',
    category: 'gdp_growth',
    subcategory: 'Growth',
    goodDirection: 'up',
  },
  {
    id: 'output_gap',
    name: 'Output Gap',
    description: 'Difference between actual and potential GDP as % of potential',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'IMF',
    imfCode: 'NGAP_NPGDP',
    category: 'gdp_growth',
    subcategory: 'Growth',
    goodDirection: 'neutral',
  },
  {
    id: 'industrial_production',
    name: 'Industrial Production Index',
    description: 'Index of industrial production (2015=100)',
    unit: 'Index',
    scale: 1,
    frequency: 'monthly',
    source: 'CBU',
    category: 'gdp_growth',
    subcategory: 'Activity',
    goodDirection: 'up',
  },
  {
    id: 'industrial_production_growth',
    name: 'Industrial Production Growth',
    description: 'Year-on-year growth in industrial production',
    unit: '%',
    scale: 1,
    frequency: 'monthly',
    source: 'CBU',
    category: 'gdp_growth',
    subcategory: 'Activity',
    goodDirection: 'up',
  },
  {
    id: 'construction_output',
    name: 'Construction Output Index',
    description: 'Index of construction activity',
    unit: 'Index',
    scale: 1,
    frequency: 'quarterly',
    source: 'CBU',
    category: 'gdp_growth',
    subcategory: 'Activity',
    goodDirection: 'up',
  },
  {
    id: 'retail_sales_growth',
    name: 'Retail Sales Growth',
    description: 'Year-on-year growth in retail trade turnover',
    unit: '%',
    scale: 1,
    frequency: 'monthly',
    source: 'CBU',
    category: 'gdp_growth',
    subcategory: 'Activity',
    goodDirection: 'up',
  },
  {
    id: 'pmi_manufacturing',
    name: 'Manufacturing PMI',
    description: 'Purchasing Managers Index for manufacturing',
    unit: 'Index',
    scale: 1,
    frequency: 'monthly',
    source: 'Trading Economics',
    category: 'gdp_growth',
    subcategory: 'Sentiment',
    goodDirection: 'up',
    thresholds: { low: 50, high: 55 },
  },
  {
    id: 'pmi_services',
    name: 'Services PMI',
    description: 'Purchasing Managers Index for services',
    unit: 'Index',
    scale: 1,
    frequency: 'monthly',
    source: 'Trading Economics',
    category: 'gdp_growth',
    subcategory: 'Sentiment',
    goodDirection: 'up',
    thresholds: { low: 50, high: 55 },
  },
  {
    id: 'business_confidence',
    name: 'Business Confidence Index',
    description: 'Survey-based business confidence indicator',
    unit: 'Index',
    scale: 1,
    frequency: 'monthly',
    source: 'CBU',
    category: 'gdp_growth',
    subcategory: 'Sentiment',
    goodDirection: 'up',
  },
  {
    id: 'consumer_confidence',
    name: 'Consumer Confidence Index',
    description: 'Survey-based consumer confidence indicator',
    unit: 'Index',
    scale: 1,
    frequency: 'monthly',
    source: 'CBU',
    category: 'gdp_growth',
    subcategory: 'Sentiment',
    goodDirection: 'up',
  },

  // ═══════════════════════════════════════════════════════════════
  // PRICES & INFLATION (20 indicators)
  // ═══════════════════════════════════════════════════════════════
  {
    id: 'inflation_cpi',
    name: 'CPI Inflation',
    description: 'Consumer price index annual inflation rate',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'IMF',
    imfCode: 'PCPIPCH',
    wbCode: 'FP.CPI.TOTL.ZG',
    category: 'prices',
    subcategory: 'Consumer Prices',
    goodDirection: 'down',
    thresholds: { low: 2, high: 10 },
    isPrimary: true,
  },
  {
    id: 'inflation_monthly',
    name: 'Monthly Inflation',
    description: 'Month-on-month CPI change',
    unit: '%',
    scale: 1,
    frequency: 'monthly',
    source: 'CBU',
    category: 'prices',
    subcategory: 'Consumer Prices',
    goodDirection: 'down',
    isPrimary: true,
  },
  {
    id: 'inflation_core',
    name: 'Core Inflation',
    description: 'Inflation excluding food and energy',
    unit: '%',
    scale: 1,
    frequency: 'monthly',
    source: 'CBU',
    category: 'prices',
    subcategory: 'Consumer Prices',
    goodDirection: 'down',
  },
  {
    id: 'inflation_food',
    name: 'Food Inflation',
    description: 'Year-on-year change in food prices',
    unit: '%',
    scale: 1,
    frequency: 'monthly',
    source: 'CBU',
    category: 'prices',
    subcategory: 'Consumer Prices',
    goodDirection: 'down',
  },
  {
    id: 'inflation_energy',
    name: 'Energy Inflation',
    description: 'Year-on-year change in energy prices',
    unit: '%',
    scale: 1,
    frequency: 'monthly',
    source: 'CBU',
    category: 'prices',
    subcategory: 'Consumer Prices',
    goodDirection: 'neutral',
  },
  {
    id: 'inflation_services',
    name: 'Services Inflation',
    description: 'Year-on-year change in services prices',
    unit: '%',
    scale: 1,
    frequency: 'monthly',
    source: 'CBU',
    category: 'prices',
    subcategory: 'Consumer Prices',
    goodDirection: 'down',
  },
  {
    id: 'ppi',
    name: 'Producer Price Index',
    description: 'Producer price index annual change',
    unit: '%',
    scale: 1,
    frequency: 'monthly',
    source: 'CBU',
    category: 'prices',
    subcategory: 'Producer Prices',
    goodDirection: 'neutral',
  },
  {
    id: 'import_prices',
    name: 'Import Price Index',
    description: 'Index of import prices',
    unit: 'Index',
    scale: 1,
    frequency: 'quarterly',
    source: 'CBU',
    category: 'prices',
    subcategory: 'Trade Prices',
    goodDirection: 'neutral',
  },
  {
    id: 'export_prices',
    name: 'Export Price Index',
    description: 'Index of export prices',
    unit: 'Index',
    scale: 1,
    frequency: 'quarterly',
    source: 'CBU',
    category: 'prices',
    subcategory: 'Trade Prices',
    goodDirection: 'up',
  },
  {
    id: 'terms_of_trade',
    name: 'Terms of Trade',
    description: 'Ratio of export prices to import prices',
    unit: 'Index',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'TT.PRI.MRCH.XD.WD',
    category: 'prices',
    subcategory: 'Trade Prices',
    goodDirection: 'up',
  },
  {
    id: 'gdp_deflator',
    name: 'GDP Deflator',
    description: 'Annual change in GDP deflator',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'NY.GDP.DEFL.KD.ZG',
    category: 'prices',
    subcategory: 'Deflators',
    goodDirection: 'neutral',
  },
  {
    id: 'house_price_index',
    name: 'House Price Index',
    description: 'Residential property price index',
    unit: 'Index',
    scale: 1,
    frequency: 'quarterly',
    source: 'CBU',
    category: 'prices',
    subcategory: 'Asset Prices',
    goodDirection: 'neutral',
  },
  {
    id: 'cpi_index',
    name: 'Consumer Price Index',
    description: 'Consumer price index level (2015=100)',
    unit: 'Index',
    scale: 1,
    frequency: 'monthly',
    source: 'CBU',
    category: 'prices',
    subcategory: 'Consumer Prices',
    goodDirection: 'neutral',
  },
  {
    id: 'inflation_expectations',
    name: 'Inflation Expectations',
    description: 'Survey-based inflation expectations for next 12 months',
    unit: '%',
    scale: 1,
    frequency: 'monthly',
    source: 'CBU',
    category: 'prices',
    subcategory: 'Expectations',
    goodDirection: 'down',
  },
  {
    id: 'wage_growth_nominal',
    name: 'Nominal Wage Growth',
    description: 'Year-on-year growth in average nominal wages',
    unit: '%',
    scale: 1,
    frequency: 'monthly',
    source: 'CBU',
    category: 'prices',
    subcategory: 'Wages',
    goodDirection: 'up',
  },
  {
    id: 'wage_growth_real',
    name: 'Real Wage Growth',
    description: 'Year-on-year growth in inflation-adjusted wages',
    unit: '%',
    scale: 1,
    frequency: 'monthly',
    source: 'CBU',
    category: 'prices',
    subcategory: 'Wages',
    goodDirection: 'up',
  },
  {
    id: 'unit_labor_cost',
    name: 'Unit Labor Cost',
    description: 'Labor cost per unit of output',
    unit: 'Index',
    scale: 1,
    frequency: 'annual',
    source: 'CBU',
    category: 'prices',
    subcategory: 'Wages',
    goodDirection: 'neutral',
  },
  {
    id: 'minimum_wage',
    name: 'Minimum Wage',
    description: 'Monthly minimum wage in local currency',
    unit: 'UZS',
    scale: 1,
    frequency: 'annual',
    source: 'CBU',
    category: 'prices',
    subcategory: 'Wages',
    goodDirection: 'up',
  },
  {
    id: 'average_wage',
    name: 'Average Monthly Wage',
    description: 'Average monthly nominal wage',
    unit: 'UZS millions',
    scale: 1e6,
    frequency: 'monthly',
    source: 'CBU',
    category: 'prices',
    subcategory: 'Wages',
    goodDirection: 'up',
  },
  {
    id: 'inflation_target',
    name: 'Inflation Target',
    description: 'Central bank inflation target',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'CBU',
    category: 'prices',
    subcategory: 'Policy',
    goodDirection: 'neutral',
  },

  // ═══════════════════════════════════════════════════════════════
  // EXTERNAL SECTOR (30 indicators)
  // ═══════════════════════════════════════════════════════════════
  {
    id: 'current_account',
    name: 'Current Account Balance',
    description: 'Current account balance in US dollars',
    unit: 'USD billions',
    scale: 1e9,
    frequency: 'annual',
    source: 'IMF',
    imfCode: 'BCA',
    wbCode: 'BN.CAB.XOKA.CD',
    category: 'external',
    subcategory: 'Balance of Payments',
    goodDirection: 'neutral',
    isPrimary: true,
  },
  {
    id: 'current_account_gdp',
    name: 'Current Account (% of GDP)',
    description: 'Current account balance as share of GDP',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'IMF',
    imfCode: 'BCA_NGDPD',
    wbCode: 'BN.CAB.XOKA.GD.ZS',
    category: 'external',
    subcategory: 'Balance of Payments',
    goodDirection: 'neutral',
    thresholds: { low: -5, high: 5 },
    isPrimary: true,
  },
  {
    id: 'trade_balance',
    name: 'Trade Balance',
    description: 'Merchandise trade balance',
    unit: 'USD billions',
    scale: 1e9,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'NE.RSB.GNFS.CD',
    category: 'external',
    subcategory: 'Trade',
    goodDirection: 'up',
    isPrimary: true,
  },
  {
    id: 'exports',
    name: 'Exports of Goods & Services',
    description: 'Total exports of goods and services',
    unit: 'USD billions',
    scale: 1e9,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'NE.EXP.GNFS.CD',
    category: 'external',
    subcategory: 'Trade',
    goodDirection: 'up',
    isPrimary: true,
  },
  {
    id: 'imports',
    name: 'Imports of Goods & Services',
    description: 'Total imports of goods and services',
    unit: 'USD billions',
    scale: 1e9,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'NE.IMP.GNFS.CD',
    category: 'external',
    subcategory: 'Trade',
    goodDirection: 'neutral',
  },
  {
    id: 'exports_gdp',
    name: 'Exports (% of GDP)',
    description: 'Exports of goods and services as share of GDP',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'NE.EXP.GNFS.ZS',
    category: 'external',
    subcategory: 'Trade',
    goodDirection: 'up',
  },
  {
    id: 'imports_gdp',
    name: 'Imports (% of GDP)',
    description: 'Imports of goods and services as share of GDP',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'NE.IMP.GNFS.ZS',
    category: 'external',
    subcategory: 'Trade',
    goodDirection: 'neutral',
  },
  {
    id: 'trade_openness',
    name: 'Trade Openness',
    description: 'Sum of exports and imports as share of GDP',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'NE.TRD.GNFS.ZS',
    category: 'external',
    subcategory: 'Trade',
    goodDirection: 'up',
  },
  {
    id: 'export_growth',
    name: 'Export Growth',
    description: 'Year-on-year growth in exports',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    category: 'external',
    subcategory: 'Trade',
    goodDirection: 'up',
  },
  {
    id: 'import_growth',
    name: 'Import Growth',
    description: 'Year-on-year growth in imports',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    category: 'external',
    subcategory: 'Trade',
    goodDirection: 'neutral',
  },
  {
    id: 'fdi_inflows',
    name: 'FDI Inflows',
    description: 'Foreign direct investment net inflows',
    unit: 'USD billions',
    scale: 1e9,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'BX.KLT.DINV.CD.WD',
    category: 'external',
    subcategory: 'Capital Flows',
    goodDirection: 'up',
    isPrimary: true,
  },
  {
    id: 'fdi_gdp',
    name: 'FDI (% of GDP)',
    description: 'Foreign direct investment net inflows as share of GDP',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'BX.KLT.DINV.WD.GD.ZS',
    category: 'external',
    subcategory: 'Capital Flows',
    goodDirection: 'up',
  },
  {
    id: 'fdi_outflows',
    name: 'FDI Outflows',
    description: 'Foreign direct investment net outflows',
    unit: 'USD billions',
    scale: 1e9,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'BM.KLT.DINV.CD.WD',
    category: 'external',
    subcategory: 'Capital Flows',
    goodDirection: 'neutral',
  },
  {
    id: 'portfolio_investment',
    name: 'Portfolio Investment',
    description: 'Net portfolio equity and debt inflows',
    unit: 'USD billions',
    scale: 1e9,
    frequency: 'annual',
    source: 'World Bank',
    category: 'external',
    subcategory: 'Capital Flows',
    goodDirection: 'neutral',
  },
  {
    id: 'remittances',
    name: 'Remittances',
    description: 'Personal remittances received',
    unit: 'USD billions',
    scale: 1e9,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'BX.TRF.PWKR.CD.DT',
    category: 'external',
    subcategory: 'Transfers',
    goodDirection: 'up',
    isPrimary: true,
  },
  {
    id: 'remittances_gdp',
    name: 'Remittances (% of GDP)',
    description: 'Personal remittances as share of GDP',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'BX.TRF.PWKR.DT.GD.ZS',
    category: 'external',
    subcategory: 'Transfers',
    goodDirection: 'neutral',
  },
  {
    id: 'fx_reserves',
    name: 'Foreign Exchange Reserves',
    description: 'Total foreign exchange reserves including gold',
    unit: 'USD billions',
    scale: 1e9,
    frequency: 'monthly',
    source: 'CBU',
    wbCode: 'FI.RES.TOTL.CD',
    category: 'external',
    subcategory: 'Reserves',
    goodDirection: 'up',
    isPrimary: true,
  },
  {
    id: 'reserve_adequacy',
    name: 'Reserve Adequacy',
    description: 'Months of import cover',
    unit: 'months',
    scale: 1,
    frequency: 'monthly',
    source: 'CBU',
    category: 'external',
    subcategory: 'Reserves',
    goodDirection: 'up',
    thresholds: { low: 3, high: 6 },
  },
  {
    id: 'gold_reserves',
    name: 'Gold Reserves',
    description: 'Gold reserves in tonnes',
    unit: 'tonnes',
    scale: 1,
    frequency: 'monthly',
    source: 'CBU',
    category: 'external',
    subcategory: 'Reserves',
    goodDirection: 'up',
  },
  {
    id: 'exchange_rate',
    name: 'Exchange Rate (USD/UZS)',
    description: 'Official exchange rate to US dollar',
    unit: 'UZS per USD',
    scale: 1,
    frequency: 'daily',
    source: 'CBU',
    wbCode: 'PA.NUS.FCRF',
    category: 'external',
    subcategory: 'Exchange Rate',
    goodDirection: 'neutral',
    isPrimary: true,
  },
  {
    id: 'exchange_rate_euro',
    name: 'Exchange Rate (EUR/UZS)',
    description: 'Official exchange rate to Euro',
    unit: 'UZS per EUR',
    scale: 1,
    frequency: 'daily',
    source: 'CBU',
    category: 'external',
    subcategory: 'Exchange Rate',
    goodDirection: 'neutral',
  },
  {
    id: 'exchange_rate_rub',
    name: 'Exchange Rate (RUB/UZS)',
    description: 'Official exchange rate to Russian Ruble',
    unit: 'UZS per RUB',
    scale: 1,
    frequency: 'daily',
    source: 'CBU',
    category: 'external',
    subcategory: 'Exchange Rate',
    goodDirection: 'neutral',
  },
  {
    id: 'reer',
    name: 'Real Effective Exchange Rate',
    description: 'Trade-weighted real effective exchange rate index',
    unit: 'Index',
    scale: 1,
    frequency: 'monthly',
    source: 'IMF',
    category: 'external',
    subcategory: 'Exchange Rate',
    goodDirection: 'neutral',
  },
  {
    id: 'neer',
    name: 'Nominal Effective Exchange Rate',
    description: 'Trade-weighted nominal effective exchange rate index',
    unit: 'Index',
    scale: 1,
    frequency: 'monthly',
    source: 'IMF',
    category: 'external',
    subcategory: 'Exchange Rate',
    goodDirection: 'neutral',
  },
  {
    id: 'external_debt',
    name: 'External Debt',
    description: 'Total external debt stock',
    unit: 'USD billions',
    scale: 1e9,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'DT.DOD.DECT.CD',
    category: 'external',
    subcategory: 'Debt',
    goodDirection: 'down',
    isPrimary: true,
  },
  {
    id: 'external_debt_gdp',
    name: 'External Debt (% of GDP)',
    description: 'Total external debt as share of GDP',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'DT.DOD.DECT.GN.ZS',
    category: 'external',
    subcategory: 'Debt',
    goodDirection: 'down',
    thresholds: { low: 30, high: 60 },
  },
  {
    id: 'external_debt_exports',
    name: 'External Debt (% of Exports)',
    description: 'Total external debt as share of exports',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'DT.DOD.DECT.EX.ZS',
    category: 'external',
    subcategory: 'Debt',
    goodDirection: 'down',
  },
  {
    id: 'debt_service_exports',
    name: 'Debt Service (% of Exports)',
    description: 'Total debt service as share of exports',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'DT.TDS.DECT.EX.ZS',
    category: 'external',
    subcategory: 'Debt',
    goodDirection: 'down',
    thresholds: { low: 10, high: 20 },
  },
  {
    id: 'short_term_debt',
    name: 'Short-term Debt (% of Reserves)',
    description: 'Short-term external debt as share of reserves',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'DT.DOD.DSTC.IR.ZS',
    category: 'external',
    subcategory: 'Debt',
    goodDirection: 'down',
    thresholds: { low: 50, high: 100 },
  },
  {
    id: 'net_international_investment',
    name: 'Net International Investment Position',
    description: 'Net international investment position',
    unit: 'USD billions',
    scale: 1e9,
    frequency: 'annual',
    source: 'IMF',
    category: 'external',
    subcategory: 'Balance of Payments',
    goodDirection: 'neutral',
  },

  // ═══════════════════════════════════════════════════════════════
  // FISCAL (25 indicators)
  // ═══════════════════════════════════════════════════════════════
  {
    id: 'govt_revenue',
    name: 'Government Revenue',
    description: 'Total government revenue',
    unit: 'UZS trillions',
    scale: 1e12,
    frequency: 'annual',
    source: 'IMF',
    imfCode: 'GGR',
    category: 'fiscal',
    subcategory: 'Revenue',
    goodDirection: 'up',
    isPrimary: true,
  },
  {
    id: 'govt_revenue_gdp',
    name: 'Government Revenue (% of GDP)',
    description: 'Total government revenue as share of GDP',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'IMF',
    imfCode: 'GGR_NGDP',
    category: 'fiscal',
    subcategory: 'Revenue',
    goodDirection: 'neutral',
  },
  {
    id: 'tax_revenue_gdp',
    name: 'Tax Revenue (% of GDP)',
    description: 'Total tax revenue as share of GDP',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'GC.TAX.TOTL.GD.ZS',
    category: 'fiscal',
    subcategory: 'Revenue',
    goodDirection: 'neutral',
  },
  {
    id: 'govt_expenditure',
    name: 'Government Expenditure',
    description: 'Total government expenditure',
    unit: 'UZS trillions',
    scale: 1e12,
    frequency: 'annual',
    source: 'IMF',
    imfCode: 'GGX',
    category: 'fiscal',
    subcategory: 'Expenditure',
    goodDirection: 'neutral',
    isPrimary: true,
  },
  {
    id: 'govt_expenditure_gdp',
    name: 'Government Expenditure (% of GDP)',
    description: 'Total government expenditure as share of GDP',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'IMF',
    imfCode: 'GGX_NGDP',
    category: 'fiscal',
    subcategory: 'Expenditure',
    goodDirection: 'neutral',
  },
  {
    id: 'fiscal_balance',
    name: 'Fiscal Balance',
    description: 'Overall fiscal balance (surplus/deficit)',
    unit: 'UZS trillions',
    scale: 1e12,
    frequency: 'annual',
    source: 'IMF',
    imfCode: 'GGXCNL',
    category: 'fiscal',
    subcategory: 'Balance',
    goodDirection: 'up',
    isPrimary: true,
  },
  {
    id: 'fiscal_balance_gdp',
    name: 'Fiscal Balance (% of GDP)',
    description: 'Overall fiscal balance as share of GDP',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'IMF',
    imfCode: 'GGXCNL_NGDP',
    category: 'fiscal',
    subcategory: 'Balance',
    goodDirection: 'up',
    thresholds: { low: -5, high: 0 },
    isPrimary: true,
  },
  {
    id: 'primary_balance_gdp',
    name: 'Primary Balance (% of GDP)',
    description: 'Primary fiscal balance as share of GDP',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'IMF',
    imfCode: 'GGXONLB_NGDP',
    category: 'fiscal',
    subcategory: 'Balance',
    goodDirection: 'up',
  },
  {
    id: 'govt_debt',
    name: 'Government Debt',
    description: 'Total general government gross debt',
    unit: 'UZS trillions',
    scale: 1e12,
    frequency: 'annual',
    source: 'IMF',
    imfCode: 'GGXWDG',
    category: 'fiscal',
    subcategory: 'Debt',
    goodDirection: 'down',
    isPrimary: true,
  },
  {
    id: 'govt_debt_gdp',
    name: 'Government Debt (% of GDP)',
    description: 'General government gross debt as share of GDP',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'IMF',
    imfCode: 'GGXWDG_NGDP',
    wbCode: 'GC.DOD.TOTL.GD.ZS',
    category: 'fiscal',
    subcategory: 'Debt',
    goodDirection: 'down',
    thresholds: { low: 40, high: 60 },
    isPrimary: true,
  },
  {
    id: 'govt_net_debt_gdp',
    name: 'Net Government Debt (% of GDP)',
    description: 'General government net debt as share of GDP',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'IMF',
    imfCode: 'GGXWDN_NGDP',
    category: 'fiscal',
    subcategory: 'Debt',
    goodDirection: 'down',
  },
  {
    id: 'interest_payments_gdp',
    name: 'Interest Payments (% of GDP)',
    description: 'Government interest payments as share of GDP',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'IMF',
    category: 'fiscal',
    subcategory: 'Debt',
    goodDirection: 'down',
  },
  {
    id: 'interest_payments_revenue',
    name: 'Interest Payments (% of Revenue)',
    description: 'Government interest payments as share of revenue',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'IMF',
    category: 'fiscal',
    subcategory: 'Debt',
    goodDirection: 'down',
    thresholds: { low: 10, high: 20 },
  },
  {
    id: 'education_expenditure',
    name: 'Education Expenditure (% of GDP)',
    description: 'Government expenditure on education as share of GDP',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'SE.XPD.TOTL.GD.ZS',
    category: 'fiscal',
    subcategory: 'Social Spending',
    goodDirection: 'up',
  },
  {
    id: 'health_expenditure',
    name: 'Health Expenditure (% of GDP)',
    description: 'Current health expenditure as share of GDP',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'SH.XPD.CHEX.GD.ZS',
    category: 'fiscal',
    subcategory: 'Social Spending',
    goodDirection: 'up',
  },
  {
    id: 'military_expenditure',
    name: 'Military Expenditure (% of GDP)',
    description: 'Military expenditure as share of GDP',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'MS.MIL.XPND.GD.ZS',
    category: 'fiscal',
    subcategory: 'Sectoral Spending',
    goodDirection: 'neutral',
  },
  {
    id: 'capital_expenditure_gdp',
    name: 'Capital Expenditure (% of GDP)',
    description: 'Government capital expenditure as share of GDP',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'CBU',
    category: 'fiscal',
    subcategory: 'Expenditure',
    goodDirection: 'up',
  },
  {
    id: 'subsidies_transfers_gdp',
    name: 'Subsidies & Transfers (% of GDP)',
    description: 'Government subsidies and transfers as share of GDP',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'CBU',
    category: 'fiscal',
    subcategory: 'Expenditure',
    goodDirection: 'neutral',
  },
  {
    id: 'public_sector_wage_bill',
    name: 'Public Sector Wage Bill (% of GDP)',
    description: 'Government compensation of employees as share of GDP',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'IMF',
    category: 'fiscal',
    subcategory: 'Expenditure',
    goodDirection: 'neutral',
  },
  {
    id: 'vat_rate',
    name: 'VAT Rate',
    description: 'Standard value added tax rate',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'NATEX',
    category: 'fiscal',
    subcategory: 'Tax Rates',
    goodDirection: 'neutral',
  },
  {
    id: 'corporate_tax_rate',
    name: 'Corporate Tax Rate',
    description: 'Standard corporate income tax rate',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'NATEX',
    category: 'fiscal',
    subcategory: 'Tax Rates',
    goodDirection: 'neutral',
  },
  {
    id: 'personal_tax_rate',
    name: 'Personal Income Tax Rate',
    description: 'Top marginal personal income tax rate',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'NATEX',
    category: 'fiscal',
    subcategory: 'Tax Rates',
    goodDirection: 'neutral',
  },
  {
    id: 'social_security_rate',
    name: 'Social Security Contribution Rate',
    description: 'Combined employer and employee social security rate',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'NATEX',
    category: 'fiscal',
    subcategory: 'Tax Rates',
    goodDirection: 'neutral',
  },
  {
    id: 'govt_effectiveness',
    name: 'Government Effectiveness',
    description: 'World Bank governance indicator for government effectiveness',
    unit: 'Score',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'GE.EST',
    category: 'fiscal',
    subcategory: 'Governance',
    goodDirection: 'up',
  },
  {
    id: 'regulatory_quality',
    name: 'Regulatory Quality',
    description: 'World Bank governance indicator for regulatory quality',
    unit: 'Score',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'RQ.EST',
    category: 'fiscal',
    subcategory: 'Governance',
    goodDirection: 'up',
  },

  // ═══════════════════════════════════════════════════════════════
  // MONETARY & FINANCIAL (25 indicators)
  // ═══════════════════════════════════════════════════════════════
  {
    id: 'policy_rate',
    name: 'Policy Rate',
    description: 'Central bank main policy interest rate',
    unit: '%',
    scale: 1,
    frequency: 'monthly',
    source: 'CBU',
    category: 'monetary',
    subcategory: 'Interest Rates',
    goodDirection: 'neutral',
    isPrimary: true,
  },
  {
    id: 'deposit_rate',
    name: 'Deposit Rate',
    description: 'Average deposit interest rate',
    unit: '%',
    scale: 1,
    frequency: 'monthly',
    source: 'CBU',
    wbCode: 'FR.INR.DPST',
    category: 'monetary',
    subcategory: 'Interest Rates',
    goodDirection: 'neutral',
  },
  {
    id: 'lending_rate',
    name: 'Lending Rate',
    description: 'Average lending interest rate',
    unit: '%',
    scale: 1,
    frequency: 'monthly',
    source: 'CBU',
    wbCode: 'FR.INR.LEND',
    category: 'monetary',
    subcategory: 'Interest Rates',
    goodDirection: 'neutral',
  },
  {
    id: 'real_interest_rate',
    name: 'Real Interest Rate',
    description: 'Lending rate adjusted for inflation',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'FR.INR.RINR',
    category: 'monetary',
    subcategory: 'Interest Rates',
    goodDirection: 'neutral',
  },
  {
    id: 'interbank_rate',
    name: 'Interbank Rate',
    description: 'Overnight interbank lending rate',
    unit: '%',
    scale: 1,
    frequency: 'daily',
    source: 'CBU',
    category: 'monetary',
    subcategory: 'Interest Rates',
    goodDirection: 'neutral',
  },
  {
    id: 'treasury_yield_3m',
    name: '3-Month Treasury Yield',
    description: 'Government treasury bill yield (3 month)',
    unit: '%',
    scale: 1,
    frequency: 'monthly',
    source: 'CBU',
    category: 'monetary',
    subcategory: 'Interest Rates',
    goodDirection: 'neutral',
  },
  {
    id: 'treasury_yield_1y',
    name: '1-Year Treasury Yield',
    description: 'Government treasury bond yield (1 year)',
    unit: '%',
    scale: 1,
    frequency: 'monthly',
    source: 'CBU',
    category: 'monetary',
    subcategory: 'Interest Rates',
    goodDirection: 'neutral',
  },
  {
    id: 'treasury_yield_10y',
    name: '10-Year Treasury Yield',
    description: 'Government treasury bond yield (10 year)',
    unit: '%',
    scale: 1,
    frequency: 'monthly',
    source: 'CBU',
    category: 'monetary',
    subcategory: 'Interest Rates',
    goodDirection: 'neutral',
  },
  {
    id: 'money_supply_m0',
    name: 'Money Supply (M0)',
    description: 'Monetary base (currency in circulation)',
    unit: 'UZS trillions',
    scale: 1e12,
    frequency: 'monthly',
    source: 'CBU',
    category: 'monetary',
    subcategory: 'Money Supply',
    goodDirection: 'neutral',
  },
  {
    id: 'money_supply_m2',
    name: 'Money Supply (M2)',
    description: 'Broad money supply',
    unit: 'UZS trillions',
    scale: 1e12,
    frequency: 'monthly',
    source: 'CBU',
    wbCode: 'FM.LBL.BMNY.GD.ZS',
    category: 'monetary',
    subcategory: 'Money Supply',
    goodDirection: 'neutral',
    isPrimary: true,
  },
  {
    id: 'money_supply_growth',
    name: 'M2 Growth',
    description: 'Year-on-year growth in broad money supply',
    unit: '%',
    scale: 1,
    frequency: 'monthly',
    source: 'CBU',
    category: 'monetary',
    subcategory: 'Money Supply',
    goodDirection: 'neutral',
  },
  {
    id: 'domestic_credit',
    name: 'Domestic Credit to Private Sector',
    description: 'Domestic credit to private sector',
    unit: 'UZS trillions',
    scale: 1e12,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'FS.AST.PRVT.GD.ZS',
    category: 'monetary',
    subcategory: 'Credit',
    goodDirection: 'up',
  },
  {
    id: 'domestic_credit_gdp',
    name: 'Domestic Credit (% of GDP)',
    description: 'Domestic credit to private sector as share of GDP',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'FS.AST.PRVT.GD.ZS',
    category: 'monetary',
    subcategory: 'Credit',
    goodDirection: 'up',
    isPrimary: true,
  },
  {
    id: 'credit_growth',
    name: 'Credit Growth',
    description: 'Year-on-year growth in bank credit',
    unit: '%',
    scale: 1,
    frequency: 'monthly',
    source: 'CBU',
    category: 'monetary',
    subcategory: 'Credit',
    goodDirection: 'up',
  },
  {
    id: 'bank_assets',
    name: 'Banking Sector Assets',
    description: 'Total assets of banking sector',
    unit: 'UZS trillions',
    scale: 1e12,
    frequency: 'quarterly',
    source: 'CBU',
    category: 'monetary',
    subcategory: 'Banking',
    goodDirection: 'up',
  },
  {
    id: 'bank_capital_ratio',
    name: 'Bank Capital Adequacy Ratio',
    description: 'Capital adequacy ratio of banking sector',
    unit: '%',
    scale: 1,
    frequency: 'quarterly',
    source: 'CBU',
    wbCode: 'FB.BNK.CAPA.ZS',
    category: 'monetary',
    subcategory: 'Banking',
    goodDirection: 'up',
    thresholds: { low: 8, high: 15 },
  },
  {
    id: 'npl_ratio',
    name: 'Non-Performing Loans Ratio',
    description: 'Non-performing loans as share of total loans',
    unit: '%',
    scale: 1,
    frequency: 'quarterly',
    source: 'CBU',
    wbCode: 'FB.AST.NPER.ZS',
    category: 'monetary',
    subcategory: 'Banking',
    goodDirection: 'down',
    thresholds: { low: 3, high: 10 },
    isPrimary: true,
  },
  {
    id: 'bank_roa',
    name: 'Bank Return on Assets',
    description: 'Return on assets of banking sector',
    unit: '%',
    scale: 1,
    frequency: 'quarterly',
    source: 'CBU',
    category: 'monetary',
    subcategory: 'Banking',
    goodDirection: 'up',
  },
  {
    id: 'bank_roe',
    name: 'Bank Return on Equity',
    description: 'Return on equity of banking sector',
    unit: '%',
    scale: 1,
    frequency: 'quarterly',
    source: 'CBU',
    category: 'monetary',
    subcategory: 'Banking',
    goodDirection: 'up',
  },
  {
    id: 'liquidity_ratio',
    name: 'Banking Sector Liquidity Ratio',
    description: 'Liquid assets to total assets ratio',
    unit: '%',
    scale: 1,
    frequency: 'quarterly',
    source: 'CBU',
    category: 'monetary',
    subcategory: 'Banking',
    goodDirection: 'up',
  },
  {
    id: 'loan_deposit_ratio',
    name: 'Loan to Deposit Ratio',
    description: 'Total loans as share of total deposits',
    unit: '%',
    scale: 1,
    frequency: 'quarterly',
    source: 'CBU',
    category: 'monetary',
    subcategory: 'Banking',
    goodDirection: 'neutral',
    thresholds: { low: 80, high: 120 },
  },
  {
    id: 'stock_market_cap',
    name: 'Stock Market Capitalization',
    description: 'Market capitalization of listed companies',
    unit: 'USD billions',
    scale: 1e9,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'CM.MKT.LCAP.CD',
    category: 'monetary',
    subcategory: 'Capital Markets',
    goodDirection: 'up',
  },
  {
    id: 'stock_market_cap_gdp',
    name: 'Stock Market Cap (% of GDP)',
    description: 'Market capitalization as share of GDP',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'CM.MKT.LCAP.GD.ZS',
    category: 'monetary',
    subcategory: 'Capital Markets',
    goodDirection: 'up',
  },
  {
    id: 'financial_inclusion',
    name: 'Financial Inclusion Index',
    description: 'Account ownership at financial institution (% age 15+)',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'FX.OWN.TOTL.ZS',
    category: 'monetary',
    subcategory: 'Inclusion',
    goodDirection: 'up',
  },
  {
    id: 'mobile_money',
    name: 'Mobile Money Accounts',
    description: 'Mobile money account ownership (% age 15+)',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    category: 'monetary',
    subcategory: 'Inclusion',
    goodDirection: 'up',
  },

  // ═══════════════════════════════════════════════════════════════
  // LABOR MARKET (20 indicators)
  // ═══════════════════════════════════════════════════════════════
  {
    id: 'unemployment_rate',
    name: 'Unemployment Rate',
    description: 'Unemployment as share of labor force',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'IMF',
    imfCode: 'LUR',
    wbCode: 'SL.UEM.TOTL.ZS',
    category: 'labor',
    subcategory: 'Employment',
    goodDirection: 'down',
    thresholds: { low: 4, high: 10 },
    isPrimary: true,
  },
  {
    id: 'youth_unemployment',
    name: 'Youth Unemployment Rate',
    description: 'Unemployment rate for ages 15-24',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'SL.UEM.1524.ZS',
    category: 'labor',
    subcategory: 'Employment',
    goodDirection: 'down',
    isPrimary: true,
  },
  {
    id: 'employment_rate',
    name: 'Employment to Population Ratio',
    description: 'Employment as share of working-age population',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'SL.EMP.TOTL.SP.ZS',
    category: 'labor',
    subcategory: 'Employment',
    goodDirection: 'up',
  },
  {
    id: 'labor_force_participation',
    name: 'Labor Force Participation Rate',
    description: 'Labor force as share of working-age population',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'SL.TLF.CACT.ZS',
    category: 'labor',
    subcategory: 'Labor Force',
    goodDirection: 'up',
    isPrimary: true,
  },
  {
    id: 'female_labor_participation',
    name: 'Female Labor Participation',
    description: 'Female labor force participation rate',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'SL.TLF.CACT.FE.ZS',
    category: 'labor',
    subcategory: 'Labor Force',
    goodDirection: 'up',
  },
  {
    id: 'labor_force_total',
    name: 'Labor Force (Total)',
    description: 'Total labor force',
    unit: 'millions',
    scale: 1e6,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'SL.TLF.TOTL.IN',
    category: 'labor',
    subcategory: 'Labor Force',
    goodDirection: 'up',
  },
  {
    id: 'labor_force_growth',
    name: 'Labor Force Growth',
    description: 'Annual growth in labor force',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    category: 'labor',
    subcategory: 'Labor Force',
    goodDirection: 'up',
  },
  {
    id: 'employment_agriculture',
    name: 'Employment in Agriculture',
    description: 'Share of employment in agriculture',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'SL.AGR.EMPL.ZS',
    category: 'labor',
    subcategory: 'Sectoral Employment',
    goodDirection: 'neutral',
  },
  {
    id: 'employment_industry',
    name: 'Employment in Industry',
    description: 'Share of employment in industry',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'SL.IND.EMPL.ZS',
    category: 'labor',
    subcategory: 'Sectoral Employment',
    goodDirection: 'up',
  },
  {
    id: 'employment_services',
    name: 'Employment in Services',
    description: 'Share of employment in services',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'SL.SRV.EMPL.ZS',
    category: 'labor',
    subcategory: 'Sectoral Employment',
    goodDirection: 'up',
  },
  {
    id: 'informal_employment',
    name: 'Informal Employment',
    description: 'Share of employment in informal sector',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'ILO',
    category: 'labor',
    subcategory: 'Employment Quality',
    goodDirection: 'down',
  },
  {
    id: 'vulnerable_employment',
    name: 'Vulnerable Employment',
    description: 'Own-account and contributing family workers',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'SL.EMP.VULN.ZS',
    category: 'labor',
    subcategory: 'Employment Quality',
    goodDirection: 'down',
  },
  {
    id: 'self_employment',
    name: 'Self-Employment Rate',
    description: 'Self-employed as share of total employment',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'SL.EMP.SELF.ZS',
    category: 'labor',
    subcategory: 'Employment Quality',
    goodDirection: 'neutral',
  },
  {
    id: 'labor_productivity',
    name: 'Labor Productivity',
    description: 'GDP per person employed',
    unit: 'USD',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'SL.GDP.PCAP.EM.KD',
    category: 'labor',
    subcategory: 'Productivity',
    goodDirection: 'up',
    isPrimary: true,
  },
  {
    id: 'labor_productivity_growth',
    name: 'Labor Productivity Growth',
    description: 'Annual growth in output per worker',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    category: 'labor',
    subcategory: 'Productivity',
    goodDirection: 'up',
  },
  {
    id: 'tertiary_education_labor',
    name: 'Tertiary Education in Labor Force',
    description: 'Labor force with advanced education',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'SL.TLF.ADVN.ZS',
    category: 'labor',
    subcategory: 'Skills',
    goodDirection: 'up',
  },
  {
    id: 'neet_rate',
    name: 'NEET Rate',
    description: 'Youth not in employment, education or training',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'ILO',
    category: 'labor',
    subcategory: 'Youth',
    goodDirection: 'down',
  },
  {
    id: 'migrant_workers',
    name: 'Migrant Workers Abroad',
    description: 'Estimated number of migrant workers abroad',
    unit: 'millions',
    scale: 1e6,
    frequency: 'annual',
    source: 'CBU',
    category: 'labor',
    subcategory: 'Migration',
    goodDirection: 'neutral',
  },
  {
    id: 'job_vacancies',
    name: 'Job Vacancy Rate',
    description: 'Vacant positions as share of total positions',
    unit: '%',
    scale: 1,
    frequency: 'quarterly',
    source: 'CBU',
    category: 'labor',
    subcategory: 'Labor Market Tightness',
    goodDirection: 'neutral',
  },
  {
    id: 'working_age_population',
    name: 'Working-Age Population',
    description: 'Population aged 15-64',
    unit: 'millions',
    scale: 1e6,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'SP.POP.1564.TO',
    category: 'labor',
    subcategory: 'Labor Force',
    goodDirection: 'up',
  },

  // ═══════════════════════════════════════════════════════════════
  // DEMOGRAPHICS & SOCIAL (20 indicators)
  // ═══════════════════════════════════════════════════════════════
  {
    id: 'population',
    name: 'Total Population',
    description: 'Total population',
    unit: 'millions',
    scale: 1e6,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'SP.POP.TOTL',
    category: 'demographics',
    subcategory: 'Population',
    goodDirection: 'up',
    isPrimary: true,
  },
  {
    id: 'population_growth',
    name: 'Population Growth',
    description: 'Annual population growth rate',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'SP.POP.GROW',
    category: 'demographics',
    subcategory: 'Population',
    goodDirection: 'up',
    isPrimary: true,
  },
  {
    id: 'median_age',
    name: 'Median Age',
    description: 'Median age of population',
    unit: 'years',
    scale: 1,
    frequency: 'annual',
    source: 'UN',
    category: 'demographics',
    subcategory: 'Age Structure',
    goodDirection: 'neutral',
    isPrimary: true,
  },
  {
    id: 'dependency_ratio',
    name: 'Age Dependency Ratio',
    description: 'Dependents per 100 working-age population',
    unit: 'ratio',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'SP.POP.DPND',
    category: 'demographics',
    subcategory: 'Age Structure',
    goodDirection: 'down',
  },
  {
    id: 'young_dependency',
    name: 'Young Dependency Ratio',
    description: 'Population 0-14 per 100 working-age',
    unit: 'ratio',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'SP.POP.DPND.YG',
    category: 'demographics',
    subcategory: 'Age Structure',
    goodDirection: 'neutral',
  },
  {
    id: 'old_dependency',
    name: 'Old-Age Dependency Ratio',
    description: 'Population 65+ per 100 working-age',
    unit: 'ratio',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'SP.POP.DPND.OL',
    category: 'demographics',
    subcategory: 'Age Structure',
    goodDirection: 'neutral',
  },
  {
    id: 'urbanization',
    name: 'Urban Population',
    description: 'Urban population as share of total',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'SP.URB.TOTL.IN.ZS',
    category: 'demographics',
    subcategory: 'Urbanization',
    goodDirection: 'up',
    isPrimary: true,
  },
  {
    id: 'urbanization_rate',
    name: 'Urbanization Rate',
    description: 'Annual urban population growth',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'SP.URB.GROW',
    category: 'demographics',
    subcategory: 'Urbanization',
    goodDirection: 'neutral',
  },
  {
    id: 'life_expectancy',
    name: 'Life Expectancy',
    description: 'Life expectancy at birth',
    unit: 'years',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'SP.DYN.LE00.IN',
    category: 'demographics',
    subcategory: 'Health',
    goodDirection: 'up',
    isPrimary: true,
  },
  {
    id: 'fertility_rate',
    name: 'Fertility Rate',
    description: 'Total fertility rate (births per woman)',
    unit: 'births',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'SP.DYN.TFRT.IN',
    category: 'demographics',
    subcategory: 'Health',
    goodDirection: 'neutral',
  },
  {
    id: 'infant_mortality',
    name: 'Infant Mortality Rate',
    description: 'Deaths per 1,000 live births',
    unit: 'per 1,000',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'SP.DYN.IMRT.IN',
    category: 'demographics',
    subcategory: 'Health',
    goodDirection: 'down',
  },
  {
    id: 'hdi',
    name: 'Human Development Index',
    description: 'UNDP Human Development Index',
    unit: 'Index',
    scale: 1,
    frequency: 'annual',
    source: 'UN',
    category: 'demographics',
    subcategory: 'Development',
    goodDirection: 'up',
    isPrimary: true,
  },
  {
    id: 'poverty_rate',
    name: 'Poverty Rate',
    description: 'Population below national poverty line',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'SI.POV.NAHC',
    category: 'demographics',
    subcategory: 'Development',
    goodDirection: 'down',
    isPrimary: true,
  },
  {
    id: 'poverty_215',
    name: 'Extreme Poverty Rate',
    description: 'Population below $2.15/day (2017 PPP)',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'SI.POV.DDAY',
    category: 'demographics',
    subcategory: 'Development',
    goodDirection: 'down',
  },
  {
    id: 'gini_index',
    name: 'Gini Index',
    description: 'Income inequality measure (0-100)',
    unit: 'Index',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'SI.POV.GINI',
    category: 'demographics',
    subcategory: 'Inequality',
    goodDirection: 'down',
  },
  {
    id: 'literacy_rate',
    name: 'Adult Literacy Rate',
    description: 'Literacy rate, adult total',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'SE.ADT.LITR.ZS',
    category: 'demographics',
    subcategory: 'Education',
    goodDirection: 'up',
  },
  {
    id: 'school_enrollment_secondary',
    name: 'Secondary School Enrollment',
    description: 'Gross secondary enrollment ratio',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'SE.SEC.ENRR',
    category: 'demographics',
    subcategory: 'Education',
    goodDirection: 'up',
  },
  {
    id: 'school_enrollment_tertiary',
    name: 'Tertiary School Enrollment',
    description: 'Gross tertiary enrollment ratio',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'SE.TER.ENRR',
    category: 'demographics',
    subcategory: 'Education',
    goodDirection: 'up',
  },
  {
    id: 'internet_users',
    name: 'Internet Users',
    description: 'Individuals using the Internet',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'IT.NET.USER.ZS',
    category: 'demographics',
    subcategory: 'Digital',
    goodDirection: 'up',
  },
  {
    id: 'mobile_subscriptions',
    name: 'Mobile Subscriptions',
    description: 'Mobile cellular subscriptions per 100 people',
    unit: 'per 100',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'IT.CEL.SETS.P2',
    category: 'demographics',
    subcategory: 'Digital',
    goodDirection: 'up',
  },

  // ═══════════════════════════════════════════════════════════════
  // BUSINESS ENVIRONMENT (20 indicators)
  // ═══════════════════════════════════════════════════════════════
  {
    id: 'ease_doing_business',
    name: 'Ease of Doing Business Score',
    description: 'World Bank Doing Business score (0-100)',
    unit: 'Score',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    category: 'business',
    subcategory: 'Business Climate',
    goodDirection: 'up',
    isPrimary: true,
  },
  {
    id: 'starting_business',
    name: 'Starting a Business Score',
    description: 'Ease of starting a business',
    unit: 'Score',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    category: 'business',
    subcategory: 'Business Climate',
    goodDirection: 'up',
  },
  {
    id: 'getting_credit',
    name: 'Getting Credit Score',
    description: 'Ease of getting credit',
    unit: 'Score',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    category: 'business',
    subcategory: 'Business Climate',
    goodDirection: 'up',
  },
  {
    id: 'protecting_investors',
    name: 'Protecting Minority Investors',
    description: 'Strength of minority investor protections',
    unit: 'Score',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    category: 'business',
    subcategory: 'Business Climate',
    goodDirection: 'up',
  },
  {
    id: 'paying_taxes',
    name: 'Paying Taxes Score',
    description: 'Ease of paying taxes',
    unit: 'Score',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    category: 'business',
    subcategory: 'Business Climate',
    goodDirection: 'up',
  },
  {
    id: 'trading_across_borders',
    name: 'Trading Across Borders Score',
    description: 'Ease of trading across borders',
    unit: 'Score',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    category: 'business',
    subcategory: 'Business Climate',
    goodDirection: 'up',
  },
  {
    id: 'enforcing_contracts',
    name: 'Enforcing Contracts Score',
    description: 'Efficiency of contract enforcement',
    unit: 'Score',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    category: 'business',
    subcategory: 'Business Climate',
    goodDirection: 'up',
  },
  {
    id: 'gci_score',
    name: 'Global Competitiveness Index',
    description: 'WEF Global Competitiveness Index score',
    unit: 'Score',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    category: 'business',
    subcategory: 'Competitiveness',
    goodDirection: 'up',
    isPrimary: true,
  },
  {
    id: 'gci_rank',
    name: 'GCI Global Rank',
    description: 'Global Competitiveness Index rank',
    unit: 'Rank',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    category: 'business',
    subcategory: 'Competitiveness',
    goodDirection: 'down',
  },
  {
    id: 'corruption_perception',
    name: 'Corruption Perception Index',
    description: 'Transparency International CPI score',
    unit: 'Score',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'CC.EST',
    category: 'business',
    subcategory: 'Governance',
    goodDirection: 'up',
    isPrimary: true,
  },
  {
    id: 'rule_of_law',
    name: 'Rule of Law Index',
    description: 'World Bank governance indicator',
    unit: 'Score',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'RL.EST',
    category: 'business',
    subcategory: 'Governance',
    goodDirection: 'up',
  },
  {
    id: 'political_stability',
    name: 'Political Stability Index',
    description: 'World Bank governance indicator',
    unit: 'Score',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'PV.EST',
    category: 'business',
    subcategory: 'Governance',
    goodDirection: 'up',
  },
  {
    id: 'economic_freedom',
    name: 'Economic Freedom Index',
    description: 'Heritage Foundation economic freedom score',
    unit: 'Score',
    scale: 1,
    frequency: 'annual',
    source: 'NATEX',
    category: 'business',
    subcategory: 'Freedom',
    goodDirection: 'up',
  },
  {
    id: 'innovation_index',
    name: 'Global Innovation Index',
    description: 'WIPO Global Innovation Index score',
    unit: 'Score',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    category: 'business',
    subcategory: 'Innovation',
    goodDirection: 'up',
  },
  {
    id: 'rd_expenditure',
    name: 'R&D Expenditure (% of GDP)',
    description: 'Research and development expenditure',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'GB.XPD.RSDV.GD.ZS',
    category: 'business',
    subcategory: 'Innovation',
    goodDirection: 'up',
  },
  {
    id: 'patent_applications',
    name: 'Patent Applications',
    description: 'Patent applications by residents',
    unit: 'number',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'IP.PAT.RESD',
    category: 'business',
    subcategory: 'Innovation',
    goodDirection: 'up',
  },
  {
    id: 'logistics_performance',
    name: 'Logistics Performance Index',
    description: 'World Bank LPI score',
    unit: 'Score',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'LP.LPI.OVRL.XQ',
    category: 'business',
    subcategory: 'Infrastructure',
    goodDirection: 'up',
  },
  {
    id: 'electricity_access',
    name: 'Access to Electricity',
    description: 'Population with access to electricity',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'EG.ELC.ACCS.ZS',
    category: 'business',
    subcategory: 'Infrastructure',
    goodDirection: 'up',
  },
  {
    id: 'new_businesses',
    name: 'New Business Registrations',
    description: 'New businesses registered per 1,000 people',
    unit: 'per 1,000',
    scale: 1,
    frequency: 'annual',
    source: 'World Bank',
    wbCode: 'IC.BUS.NDNS.ZS',
    category: 'business',
    subcategory: 'Entrepreneurship',
    goodDirection: 'up',
  },
  {
    id: 'sme_employment',
    name: 'SME Employment Share',
    description: 'Share of employment in SMEs',
    unit: '%',
    scale: 1,
    frequency: 'annual',
    source: 'CBU',
    category: 'business',
    subcategory: 'Entrepreneurship',
    goodDirection: 'up',
  },

  // ═══════════════════════════════════════════════════════════════
  // RATINGS & RISK (15 indicators)
  // ═══════════════════════════════════════════════════════════════
  {
    id: 'sp_rating',
    name: 'S&P Sovereign Rating',
    description: 'S&P Global sovereign credit rating',
    unit: 'Rating',
    scale: 1,
    frequency: 'annual',
    source: 'NATEX',
    category: 'ratings',
    subcategory: 'Credit Ratings',
    goodDirection: 'up',
    isPrimary: true,
  },
  {
    id: 'moodys_rating',
    name: "Moody's Sovereign Rating",
    description: "Moody's sovereign credit rating",
    unit: 'Rating',
    scale: 1,
    frequency: 'annual',
    source: 'NATEX',
    category: 'ratings',
    subcategory: 'Credit Ratings',
    goodDirection: 'up',
    isPrimary: true,
  },
  {
    id: 'fitch_rating',
    name: 'Fitch Sovereign Rating',
    description: 'Fitch sovereign credit rating',
    unit: 'Rating',
    scale: 1,
    frequency: 'annual',
    source: 'NATEX',
    category: 'ratings',
    subcategory: 'Credit Ratings',
    goodDirection: 'up',
    isPrimary: true,
  },
  {
    id: 'sp_outlook',
    name: 'S&P Rating Outlook',
    description: 'S&P sovereign rating outlook',
    unit: 'Outlook',
    scale: 1,
    frequency: 'annual',
    source: 'NATEX',
    category: 'ratings',
    subcategory: 'Credit Ratings',
    goodDirection: 'up',
  },
  {
    id: 'cds_spread',
    name: 'Sovereign CDS Spread',
    description: '5-year credit default swap spread',
    unit: 'bps',
    scale: 1,
    frequency: 'daily',
    source: 'Trading Economics',
    category: 'ratings',
    subcategory: 'Market Risk',
    goodDirection: 'down',
    isPrimary: true,
  },
  {
    id: 'sovereign_spread',
    name: 'Sovereign Bond Spread',
    description: 'Spread over US Treasury',
    unit: 'bps',
    scale: 1,
    frequency: 'daily',
    source: 'Trading Economics',
    category: 'ratings',
    subcategory: 'Market Risk',
    goodDirection: 'down',
  },
  {
    id: 'country_risk_premium',
    name: 'Country Risk Premium',
    description: 'Equity risk premium for country',
    unit: '%',
    scale: 1,
    frequency: 'monthly',
    source: 'NATEX',
    category: 'ratings',
    subcategory: 'Market Risk',
    goodDirection: 'down',
  },
  {
    id: 'political_risk',
    name: 'Political Risk Index',
    description: 'Composite political risk score',
    unit: 'Score',
    scale: 1,
    frequency: 'monthly',
    source: 'NATEX',
    category: 'ratings',
    subcategory: 'Country Risk',
    goodDirection: 'up',
  },
  {
    id: 'economic_risk',
    name: 'Economic Risk Index',
    description: 'Composite economic risk score',
    unit: 'Score',
    scale: 1,
    frequency: 'monthly',
    source: 'NATEX',
    category: 'ratings',
    subcategory: 'Country Risk',
    goodDirection: 'up',
  },
  {
    id: 'financial_risk',
    name: 'Financial Risk Index',
    description: 'Composite financial risk score',
    unit: 'Score',
    scale: 1,
    frequency: 'monthly',
    source: 'NATEX',
    category: 'ratings',
    subcategory: 'Country Risk',
    goodDirection: 'up',
  },
  {
    id: 'composite_risk',
    name: 'Composite Risk Rating',
    description: 'Overall country risk score',
    unit: 'Score',
    scale: 1,
    frequency: 'monthly',
    source: 'NATEX',
    category: 'ratings',
    subcategory: 'Country Risk',
    goodDirection: 'up',
    isPrimary: true,
  },
  {
    id: 'opportunity_score',
    name: 'NATEX Opportunity Score',
    description: 'Proprietary investment opportunity score',
    unit: 'Score',
    scale: 1,
    frequency: 'monthly',
    source: 'NATEX',
    category: 'ratings',
    subcategory: 'NATEX Scores',
    goodDirection: 'up',
    isPrimary: true,
  },
  {
    id: 'esg_score',
    name: 'ESG Score',
    description: 'Environmental, Social, Governance score',
    unit: 'Score',
    scale: 1,
    frequency: 'annual',
    source: 'NATEX',
    category: 'ratings',
    subcategory: 'ESG',
    goodDirection: 'up',
  },
  {
    id: 'environmental_score',
    name: 'Environmental Score',
    description: 'Environmental performance score',
    unit: 'Score',
    scale: 1,
    frequency: 'annual',
    source: 'NATEX',
    category: 'ratings',
    subcategory: 'ESG',
    goodDirection: 'up',
  },
  {
    id: 'social_score',
    name: 'Social Score',
    description: 'Social performance score',
    unit: 'Score',
    scale: 1,
    frequency: 'annual',
    source: 'NATEX',
    category: 'ratings',
    subcategory: 'ESG',
    goodDirection: 'up',
  },
];

// Helper functions
export const getIndicatorById = (id: string): IndicatorMetadata | undefined => {
  return INDICATOR_REGISTRY.find(ind => ind.id === id);
};

export const getIndicatorsByCategory = (category: IndicatorCategory): IndicatorMetadata[] => {
  return INDICATOR_REGISTRY.filter(ind => ind.category === category);
};

export const getPrimaryIndicators = (): IndicatorMetadata[] => {
  return INDICATOR_REGISTRY.filter(ind => ind.isPrimary);
};

export const searchIndicators = (query: string): IndicatorMetadata[] => {
  const lowerQuery = query.toLowerCase();
  return INDICATOR_REGISTRY.filter(
    ind =>
      ind.name.toLowerCase().includes(lowerQuery) ||
      ind.description.toLowerCase().includes(lowerQuery) ||
      ind.id.toLowerCase().includes(lowerQuery)
  );
};

export const getCategoryInfo = (category: IndicatorCategory): CategoryInfo | undefined => {
  return CATEGORIES.find(cat => cat.id === category);
};

export const getIndicatorCount = (): number => {
  return INDICATOR_REGISTRY.length;
};

export const getIndicatorCountByCategory = (): Record<IndicatorCategory, number> => {
  const counts = {} as Record<IndicatorCategory, number>;
  CATEGORIES.forEach(cat => {
    counts[cat.id] = getIndicatorsByCategory(cat.id).length;
  });
  return counts;
};

export default INDICATOR_REGISTRY;
